import axiosInstance from "../axiosInstance";
import { ApiResponse } from "@/types/apiResponse";
import type { Announcement } from "./announcements";
import landingStatsFallback from "@/config/stats.json";

export interface LandingStats {
  students: number;
  tests_taken: { mock: number; practice: number; total: number };
  questions: number;
  papers: number;
  subjects: number;
  topics?: number;
}

export interface LandingSubject {
  subject_id: string;
  subject_code: string;
  subject_name: string;
  paper_count: number;
}

export interface LandingChoice {
  choice_id: number;
  choice_letter: string | null;
  choice_text: string | null;
}

/** Today's question with the options visible and no way to tell which one
 *  is right — the server never sends `is_correct` here. */
export interface LandingTeaser {
  question_id: number;
  question_text: string;
  subject_code: string | null;
  subject_name: string | null;
  year: number | null;
  tags: string[];
  choices: LandingChoice[];
}

export interface LandingPayload {
  stats: LandingStats;
  subjects: LandingSubject[];
  daily: LandingTeaser | null;
  announcements: Announcement[];
}

export const landingKey = ["public", "landing"] as const;

export const getLanding = async (): Promise<ApiResponse<LandingPayload>> => {
  const res = await axiosInstance.get("/public/landing");
  return res.data;
};

/* ------------------------------------------------------ the public claims */

/**
 * Rounds a live count down to something that stays true as it grows.
 *
 * These are public claims, so they must never read higher than the truth —
 * 103 students becomes "100+", not "105". Rounding down also means the
 * figure survives until the next one without needing a correction.
 */
export function roundDown(n: number): string {
  if (n < 10) return String(n);
  const step = n < 100 ? 10 : n < 1000 ? 50 : n < 10000 ? 100 : 500;
  return `${(Math.floor(n / step) * step).toLocaleString("en-IN")}+`;
}

export interface BandStat {
  value: string;
  label: string;
  /** The one figure in the row that is a word rather than a count. */
  accent?: boolean;
}

const FALLBACK_STATS = (
  landingStatsFallback as { stats: Array<{ value: string; label: string }> }
).stats;

/**
 * The stat row shown on the landing page and inside the sign-in dialog.
 *
 * `src/config/stats.json` is the fallback, not the source: when the live
 * counters arrive the row shows those instead, so the numbers can never
 * drift behind the database the way a hand-edited file does.
 */
export function bandStats(stats: LandingStats | null | undefined): BandStat[] {
  if (!stats) {
    return FALLBACK_STATS.map((s, i) => ({
      ...s,
      accent: i === FALLBACK_STATS.length - 1,
    }));
  }
  return [
    { value: roundDown(stats.students), label: "Students" },
    { value: roundDown(stats.tests_taken.total), label: "Tests taken" },
    { value: roundDown(stats.questions), label: "Questions" },
    { value: "Beta", label: "Early access", accent: true },
  ];
}

export interface PracticeSampleChoice {
  choice_id: number;
  choice_letter: string | null;
  choice_text: string | null;
  is_correct: boolean;
}

export interface PracticeSample {
  question_id: string;
  question_text: string;
  subject_code: string | null;
  subject_name: string | null;
  year: number | null;
  topic: string | null;
  choices: PracticeSampleChoice[];
  /** Already flattened to plain sentences server-side. */
  explanation: string;
}

export const practiceSampleKey = ["public", "practice-sample"] as const;

export const getPracticeSample = async (): Promise<
  ApiResponse<PracticeSample | null>
> => {
  const res = await axiosInstance.get("/public/practice-sample");
  return res.data;
};
